import React from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  required,
  SimpleFormProps,
  useRedirect,
  SelectInput,
  FormDataConsumer,
  BooleanInput,
} from "react-admin";
import FirebaseImageInput from "../../components/FirebaseImageInput";
import firebaseDataProvider from "../../providers/dataProvider";
import { LayoutType, categoryVersionForLayout } from "../../types";

// Extend SimpleFormProps to allow the initialValues prop.
interface InitialValueFormProps extends SimpleFormProps {
  initialValues?: any;
}

// Wrap SimpleForm so that it accepts initialValues.
const InitialValueForm: React.FC<InitialValueFormProps> = (props) => (
  <SimpleForm {...props} />
);

const categoryTypeChoices = [
  { id: LayoutType.BANNER, name: "Banner" },
  { id: LayoutType.VIDEO, name: "Video" },
  { id: LayoutType.GRID, name: "Grid" },
];

const CategoryCreate: React.FC = (props) => {
  // Get the redirect function from react-admin
  const redirect = useRedirect();

  const transform = (data: any) => {
    const layoutType = data.layoutType || LayoutType.BANNER;
    let entries: any[] = [];

    if (layoutType === LayoutType.BANNER && data.images) {
      // Banner type: multiple images with referenceIds
      entries = data.images.map((item: any) => {
        const asset = item.image;
        return {
          id: `ca${Math.random().toString(36).substring(2, 15)}`,
          assets: [asset],
          referenceId: item.referenceId || "",
          type: "banner",
          title: "",
          description: "",
          displayName: "",
          artists: [],
          subItemCount: 1,
        };
      });
    } else if (layoutType === LayoutType.VIDEO || layoutType === LayoutType.GRID) {
      // Video/Grid type: empty category, shows will be added later (show-driven, not image-driven)
      entries = [];
    }

    const category: any = {
      id: `ca${Math.random().toString(36).substring(2, 15)}`,
      name: data.name || `${layoutType.charAt(0).toUpperCase() + layoutType.slice(1)} Category`,
      layoutType,
      entries,
      isPrimary: data.isPrimary ?? true,
      version: categoryVersionForLayout(layoutType),
      isAdminCreated: true,
      isWebOnly: false,
      priority: 1,
      searchIndex: (data.name || "").toLowerCase(),
    };

    // Add episodesAlwaysVisible for non-banner categories
    if (layoutType !== LayoutType.BANNER) {
      category.episodesAlwaysVisible = data.episodesAlwaysVisible ?? true;
    }

    return category;
  };

  // Custom onSuccess callback with proper resource passed to the redirect call.
  // All new categories (banner, video, etc.) are saved to sync/override/extra
  // which gets synced to the main categories collection by the sync service.
  const onSuccess = async (response: any) => {
    try {
      await firebaseDataProvider.create("sync/override/extra", {
        data: response,
      });
      console.log("Override created successfully");
    } catch (error) {
      console.error("Error creating override document", error);
    }
    // Manually trigger redirection with a resource specified
    redirect("list", "categories");
  };

  return (
    <Create
      resource="categories"
      transform={transform}
      mutationOptions={{ onSuccess }} // Overriding onSuccess disables the built-in redirect
      {...props}
    >
      <InitialValueForm initialValues={{ layoutType: LayoutType.BANNER, episodesAlwaysVisible: true, isPrimary: true }}>
        <SelectInput
          source="layoutType"
          label="Category Type"
          choices={categoryTypeChoices}
          defaultValue={LayoutType.BANNER}
          validate={required()}
          fullWidth
        />
        <TextInput source="name" label="Category Name" fullWidth validate={required()} />

        <BooleanInput
          source="isPrimary"
          label="Is Primary"
          helperText="When enabled, category will be shown on the home tab"
        />

        <FormDataConsumer>
          {({ formData }) =>
            formData && formData.layoutType === LayoutType.BANNER ? (
              <ArrayInput source="images" label="Banner Images" defaultValue={[{}]}>
                <SimpleFormIterator>
                  <FirebaseImageInput
                    source="image"
                    label="Banner"
                    validate={required()}
                  />
                  <TextInput
                    source="referenceId"
                    label="Reference URL"
                    placeholder="Enter a specific URL or referenceId"
                  />
                </SimpleFormIterator>
              </ArrayInput>
            ) : null
          }
        </FormDataConsumer>

      </InitialValueForm>
    </Create>
  );
};

export default CategoryCreate;
